import React, { useState, useEffect } from 'react';
import { getLeads, updateLead, deleteLead, subscribeToLeads, unsubscribeChannel, logActivity } from '../../services/supabaseService';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import KanbanColumn from './components/KanbanColumn';
import AssignOwnerModal from './components/AssignOwnerModal';
import AddNoteModal from './components/AddNoteModal';
import ConvertLeadModal from './components/ConvertLeadModal';
import PipelineFilters from './components/PipelineFilters';
import MobileStageView from './components/MobileStageView';

const LeadsPipeline = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [leads, setLeads] = useState([]);
  const [draggedLead, setDraggedLead] = useState(null);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [showConvertModal, setShowConvertModal] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [activeTab, setActiveTab] = useState('new');
  const [filters, setFilters] = useState({
    owner: '',
    scoreRange: '',
    fromDate: '',
    toDate: ''
  });

  useEffect(() => {
    loadLeads();

    // Subscribe to real-time leads changes
    const channel = subscribeToLeads(() => {
      loadLeads();
    });

    // Cleanup subscription on unmount
    return () => {
      unsubscribeChannel(channel);
    };
  }, []);

  const loadLeads = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getLeads();
      setLeads(data || []);
    } catch (err) {
      console.error('Leads load error:', err);
      setError(err?.message || 'Failed to load leads');
    } finally {
      setLoading(false);
    }
  };

  const stages = ['new', 'contacted', 'qualified', 'proposal', 'negotiation', 'won', 'lost'];

  const filterLeads = (leadsToFilter) => {
    return leadsToFilter?.filter(lead => {
      if (filters?.owner && lead?.assignedToProfile?.fullName !== filters?.owner) return false;
      if (filters?.scoreRange) {
        const score = lead?.score || 0;
        const [min, max] = filters?.scoreRange?.split('-')?.map(Number);
        if (score < min || score > max) return false;
      }
      if (filters?.fromDate && new Date(lead.createdAt) < new Date(filters.fromDate)) return false;
      if (filters?.toDate && new Date(lead.createdAt) > new Date(filters.toDate)) return false;
      return true;
    });
  };

  const filteredLeads = filterLeads(leads);

  const handleDragStart = (e, lead) => {
    setDraggedLead(lead);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e?.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e, newStage) => {
    e?.preventDefault();
    if (draggedLead && draggedLead?.stage !== newStage) {
      try {
        const oldStage = draggedLead?.stage;
        
        await updateLead(draggedLead?.id, { stage: newStage });
        await loadLeads();
        
        // Log activity
        await logActivity({
          module: 'leads',
          action: 'status_change',
          entityType: 'lead',
          entityId: draggedLead?.id,
          entityName: draggedLead?.companyName || 'Lead',
          description: `Moved lead from ${oldStage} to ${newStage} stage`,
          oldValue: { stage: oldStage },
          newValue: { stage: newStage }
        });
      } catch (err) {
        console.error('Stage update error:', err);
        alert('Failed to update lead stage');
      }
    }
    setDraggedLead(null);
  };

  const handleAssignOwner = (lead) => {
    setSelectedLead(lead);
    setShowAssignModal(true);
  };

  const handleAddNote = (lead) => {
    setSelectedLead(lead);
    setShowNoteModal(true);
  };

  const handleConvert = (lead) => {
    setSelectedLead(lead);
    setShowConvertModal(true);
  };

  const handleOwnerAssignment = async (leadId, ownerId) => {
    try {
      await updateLead(leadId, { assignedTo: ownerId });
      await loadLeads();
    } catch (err) {
      console.error('Owner assignment error:', err);
      alert('Failed to assign owner');
    }
  };

  const handleNoteAdd = async (leadId, note) => {
    try {
      await updateLead(leadId, { notes: note });
      await loadLeads();
    } catch (err) {
      console.error('Note add error:', err);
      alert('Failed to add note');
    }
  };

  const handleLeadConversion = async (tenantData) => {
    try {
      await deleteLead(selectedLead?.id);
      await loadLeads();
      setShowConvertModal(false);
    } catch (err) {
      console.error('Lead conversion error:', err);
      alert('Failed to convert lead');
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleResetFilters = () => {
    setFilters({
      owner: '',
      scoreRange: '',
      fromDate: '',
      toDate: ''
    });
  };

  const handleSaveAssignment = async (leadId, ownerId) => {
    try {
      const lead = leads?.find(l => l?.id === leadId);
      const oldAssignedTo = lead?.assignedTo;
      
      await updateLead(leadId, { assignedTo: ownerId });
      await loadLeads();
      setShowAssignModal(false);
      
      // Log activity
      await logActivity({
        module: 'leads',
        action: 'assignment',
        entityType: 'lead',
        entityId: leadId,
        entityName: lead?.companyName || 'Lead',
        description: 'Assigned lead to new owner',
        oldValue: { assignedTo: oldAssignedTo },
        newValue: { assignedTo: ownerId }
      });
    } catch (err) {
      console.error('Assign owner error:', err);
      alert('Failed to assign owner');
    }
  };

  const handleSaveNote = async (leadId, note) => {
    try {
      const lead = leads?.find(l => l?.id === leadId);
      const existingNotes = lead?.notes || '';
      const updatedNotes = existingNotes ? `${existingNotes}\n${note}` : note;
      
      await updateLead(leadId, { notes: updatedNotes });
      await loadLeads();
      setShowNoteModal(false);
      
      // Log activity
      await logActivity({
        module: 'leads',
        action: 'note_added',
        entityType: 'lead',
        entityId: leadId,
        entityName: lead?.companyName || 'Lead',
        description: 'Added note to lead',
        newValue: { note }
      });
    } catch (err) {
      console.error('Add note error:', err);
      alert('Failed to add note');
    }
  };

  const handleDeleteLead = async (leadId) => {
    if (!window.confirm('Are you sure you want to delete this lead?')) return;
    
    try {
      const lead = leads?.find(l => l?.id === leadId);
      
      await deleteLead(leadId);
      await loadLeads();
      
      // Log activity
      await logActivity({
        module: 'leads',
        action: 'delete',
        entityType: 'lead',
        entityId: leadId,
        entityName: lead?.companyName || 'Lead',
        description: 'Deleted lead from pipeline'
      });
    } catch (err) {
      console.error('Delete lead error:', err);
      alert('Failed to delete lead');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading leads...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-danger-50 border border-danger-200 rounded-lg p-4">
        <p className="text-danger-800">Error: {error}</p>
        <button
          onClick={loadLeads}
          className="mt-2 text-sm text-danger-600 hover:text-danger-700 underline"
        >
          Try again
        </button>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold text-foreground mb-2">
              Leads Pipeline
            </h1>
            <p className="text-sm text-muted-foreground">
              Manage and track your sales prospects through conversion stages
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              iconName="Download"
              iconPosition="left"
              size="default"
            >
              Export
            </Button>
            <Button
              variant="default"
              iconName="Plus"
              iconPosition="left"
              size="default"
            >
              Add Lead
            </Button>
          </div>
        </div>
      </div>

      <PipelineFilters
        filters={filters}
        onFilterChange={handleFilterChange}
        onReset={handleResetFilters}
      />

      <div className="hidden lg:block">
        <div className="flex gap-4 overflow-x-auto pb-4">
          {stages?.map(stage => (
            <KanbanColumn
              key={stage}
              stage={stage}
              leads={filteredLeads}
              onDragStart={handleDragStart}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              onAssignOwner={handleAssignOwner}
              onAddNote={handleAddNote}
              onConvert={handleConvert}
              draggedLead={draggedLead}
              onStageChange={handleDrop}
            />
          ))}
        </div>
      </div>

      <div className="lg:hidden">
        <div className="bg-card rounded-lg border border-border mb-4">
          <div className="flex overflow-x-auto">
            {stages?.map(stage => {
              const stageConfig = {
                'new': { label: 'New', icon: 'Inbox' },
                'contacted': { label: 'Contacted', icon: 'Phone' },
                'qualified': { label: 'Qualified', icon: 'CheckCircle' },
                'proposal': { label: 'Proposal', icon: 'FileText' },
                'negotiation': { label: 'Negotiation', icon: 'MessageSquare' },
                'lost': { label: 'Lost', icon: 'XCircle' }
              };
              const config = stageConfig?.[stage];
              const count = filteredLeads?.filter(l => l?.stage === stage)?.length;

              return (
                <button
                  key={stage}
                  onClick={() => setActiveTab(stage)}
                  className={`
                    flex-shrink-0 flex items-center gap-2 px-4 py-3 border-b-2 transition-colors
                    ${activeTab === stage
                      ? 'border-primary text-primary bg-primary/5' : 'border-transparent text-muted-foreground hover:text-foreground'
                    }
                  `}
                >
                  <Icon name={config?.icon} size={18} />
                  <span className="font-medium text-sm whitespace-nowrap">{config?.label}</span>
                  <span className="px-2 py-0.5 bg-muted rounded text-xs">{count}</span>
                </button>
              );
            })}
          </div>
        </div>

        <div className="bg-card rounded-lg border border-border">
          <MobileStageView
            stage={activeTab}
            leads={filteredLeads}
            onAssignOwner={handleAssignOwner}
            onAddNote={handleAddNote}
            onConvert={handleConvert}
          />
        </div>
      </div>

      {showAssignModal && (
        <AssignOwnerModal
          lead={selectedLead}
          owners={[]}
          onClose={() => setShowAssignModal(false)}
          onAssign={handleOwnerAssignment}
        />
      )}
      {showNoteModal && (
        <AddNoteModal
          lead={selectedLead}
          onClose={() => setShowNoteModal(false)}
          onAddNote={handleNoteAdd}
        />
      )}
      {showConvertModal && (
        <ConvertLeadModal
          lead={selectedLead}
          inventoryUnits={[]}
          onClose={() => setShowConvertModal(false)}
          onConvert={handleLeadConversion}
        />
      )}
    </div>
  );
};

export default LeadsPipeline;